import {
  type ActionOptions,
  useAction,
  useActionWithPayload,
} from '../../graphQl'
import { useAnalytics } from '../../analytics'
import {
  type Quote,
} from '../../quotes'
import Language from '../../../app/Language.enum'
import {
  useCurrentMove,
} from './moves.data.hooks'
import { type Move } from './models/move.model'
import {
  type CreateMovePayload,
  type UpdateMovePayload,
  type AcceptQuotePayload,
} from './moves.types'
import * as mutations from './moves.mutations'
import { type MoveCustomer } from './models/subModels/moveCustomer.model'

/**
 * create a move
 */
export const useCreateMoveAction = () => {
  const [mutate, data] = useActionWithPayload(
    useAction<Move>(mutations.createMove),
    (payload: CreateMovePayload, promoCode?: string) => ({ payload, promoCode }),
  )
  const { pushMoveCreatedEvent } = useAnalytics()

  const action = async (payload: CreateMovePayload, promoCode?: string) => {
    const move = await mutate(payload, promoCode)

    /* tracking */

    const originAddress = move.locations.origin?.address ?? payload.origin.address
    const destinationAddress = move.locations.destination?.address ?? payload.destination.address

    const nbQuotesReceived = move.quotes?.length ?? 0
    const fromCity = 'city' in originAddress ? originAddress.city : undefined
    const fromRegion = 'region' in originAddress ? originAddress.region : undefined
    const toCity = 'city' in destinationAddress ? destinationAddress.city : undefined
    const toRegion = 'region' in destinationAddress ? destinationAddress.region : undefined

    pushMoveCreatedEvent({
      nbQuotesReceived,
      fromCity,
      fromRegion,
      toCity,
      toRegion,
    })

    return move
  }
  return [action, data] as [typeof action, typeof data]
}

/**
 * update customer info
 */
export const useUpdateCustomerAction = () => {
  const move = useCurrentMove()

  return useActionWithPayload(
    useAction<Move>(mutations.updateCustomer),
    (payload: MoveCustomer) => ({ movePublicId: move.publicId, payload }),
  )
}

/**
 * update a move
 */
export const useUpdateMoveAction = () => {
  const move = useCurrentMove()

  const [mutate, data] = useActionWithPayload(
    useAction<Move>(mutations.updateMove),
    (payload: UpdateMovePayload) => ({
      movePublicId: move.publicId,
      payload,
    }),
  )
  const action = async (payload: UpdateMovePayload) => {
    return await mutate(payload)
  }
  return [action, data] as [typeof action, typeof data]
}

/**
 * refresh quotes
 */
export const useRefreshQuotesAction = () => {
  const move = useCurrentMove()

  return useActionWithPayload(
    useAction<Move>(mutations.refreshQuotes),
    () => ({ movePublicId: move.publicId }),
  )
}

/**
 * select/unselect a quote
 */
export const useSelectQuoteAction = () => {
  const move = useCurrentMove()
  const { pushQuoteSelectedEvent, pushQuoteUnselectedEvent } = useAnalytics()

  const [mutate, data] = useActionWithPayload(
    useAction<Move>(mutations.selectQuote),
    (quote: Quote | null) => ({
      movePublicId: move.publicId,
      quoteId: quote?.id ?? null,
    }),
  )

  const action = async (quote: Quote | null, position?: number) => {
    const move = await mutate(quote)

    /* tracking */
    if (quote) {
      pushQuoteSelectedEvent({
        companyId: quote.companyBranch.id,
        companyName: quote.companyBranch.name[Language.En],
        price: quote.subtotal,
        companyCity: quote.companyBranch.city,
        position,
      })
    } else {
      pushQuoteUnselectedEvent()
    }

    return move
  }

  return [action, data] as [typeof action, typeof data]
}

/**
 * prepare move deposit
 */
export const usePrepareMoveDepositAction = () => {
  const move = useCurrentMove()

  return useActionWithPayload(
    useAction<Move>(mutations.prepareMoveDeposit),
    () => ({ movePublicId: move.publicId }),
  )
}

/**
 * prepare move deposit
 */
export const usePrepareMoveBalancePaymentAction = () => {
  const move = useCurrentMove()

  return useActionWithPayload(
    useAction<Move>(mutations.prepareMoveBalancePayment),
    () => ({ movePublicId: move.publicId }),
  )
}

/**
 * apply promo code
 */
export const useApplyPromoCodeAction = () => {
  const move = useCurrentMove()
  const { pushPromoCodeAddedEvent, pushPromoCodeRemovedEvent } = useAnalytics()

  const [mutate, data] = useActionWithPayload(
    useAction<Move>(mutations.applyPromoCode),
    (promoCode: string | null) => ({
      movePublicId: move.publicId,
      promoCode,
    }),
  )

  const action = async (promoCode: string | null) => {
    const originalPromoCode = move.promoCode?.pendingPromoCode?.code
    const updatedMove = await mutate(promoCode)
    const newPromoCode = updatedMove.promoCode?.pendingPromoCode?.code

    /* tracking */

    if (promoCode && newPromoCode) {
      pushPromoCodeAddedEvent(newPromoCode)
    } else if (!promoCode && originalPromoCode) {
      pushPromoCodeRemovedEvent(originalPromoCode)
    }

    return move
  }

  return [action, data] as [typeof action, typeof data]
}

/**
 * accept a quote
 */
export const useAcceptQuoteAction = (options?: ActionOptions) => {
  const currentMove = useCurrentMove()
  const { pushQuoteAcceptedEvent } = useAnalytics()

  const [mutate, data] = useActionWithPayload(
    useAction<Move>(mutations.acceptQuote, options),
    (payload: AcceptQuotePayload) => ({
      movePublicId: currentMove.publicId,
      payload,
    }),
  )

  const action = async (
    payload: AcceptQuotePayload,
  ) => {
    if (!currentMove.selectedQuote) {
      throw new Error('No selected quote')
    }

    const move = await mutate(payload)

    if (move.selectedQuote) {
    /* tracking */
      pushQuoteAcceptedEvent({
        companyId: move.selectedQuote.companyBranch.id,
        companyName: move.selectedQuote.companyBranch.name[Language.En],
        price: move.selectedQuote.subtotal,
        companyCity: move.selectedQuote.companyBranch.city,
        promoCode: move.promoCode?.appliedPromoCode?.code,
      })
    }

    return move
  }

  return [action, data] as [typeof action, typeof data]
}

/**
 * upload a note attachment
 */
export const useUploadNoteAttachmentAction = () => {
  const move = useCurrentMove()

  return useActionWithPayload(
    useAction<Move>(mutations.uploadNoteAttachment),
    (file: File) => ({
      movePublicId: move.publicId,
      payload: { file },
    }),
  )
}

/**
 * delete a note attachment
 */
export const useDeleteNoteAttachmentAction = () => {
  const move = useCurrentMove()

  return useActionWithPayload(
    useAction<Move>(mutations.deleteNoteAttachment),
    (fileId: string) => ({
      movePublicId: move.publicId,
      payload: { fileId },
    }),
  )
}
